import * as React from 'react';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'gradient';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const variantMap: Record<Variant, string> = {
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/90',
  outline: 'border border-border bg-white hover:bg-muted text-raptor-navy',
  ghost: 'hover:bg-muted text-raptor-navy',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  gradient: 'text-white bg-raptor-gradient hover:opacity-90 shadow-sm',
};
const sizeMap: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-base',
  icon: 'h-9 w-9 p-0',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'disabled:opacity-50 disabled:pointer-events-none',
        variantMap[variant], sizeMap[size], className,
      )}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

// Compatibility shim for legacy Radix wrappers (e.g. alert-dialog.jsx) that
// expect a ``buttonVariants(opts)`` helper returning class strings.
export const buttonVariants = (opts?: { variant?: Variant; size?: Size; className?: string }): string => {
  const v: Variant = opts?.variant ?? 'primary';
  const s: Size = opts?.size ?? 'md';
  return cn(
    'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
    'disabled:opacity-50 disabled:pointer-events-none',
    variantMap[v], sizeMap[s], opts?.className,
  );
};
